import React, { forwardRef, useImperativeHandle, useState, useRef } from 'react';
import { Platform, ScrollView, StyleSheet } from 'react-native';
import { StyledModal, StyledModalHandle } from '@constants/Themed';
import { YStack, XStack, Text, Button, Input, TextArea, Label, Switch, H5, Separator } from 'tamagui';
import { MaterialIcons, Feather } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import DateTimePicker from '@react-native-community/datetimepicker';
import moment from 'moment';
import apis from '@api/api';

interface EditEventModalProps {
  onEventUpdated?: () => void;
}

export interface EditEventModalRef {
  showModal: (event: any) => void;
  hideModal: () => void;
}

const EditEventModal = forwardRef<EditEventModalRef, EditEventModalProps>(
  ({ onEventUpdated }, ref) => {
    const theme = themeSelector();
    const modalRef = useRef<StyledModalHandle>(null);
    const [event, setEvent] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [showStartDatePicker, setShowStartDatePicker] = useState(false);
    const [showEndDatePicker, setShowEndDatePicker] = useState(false);
    const [showStartTimePicker, setShowStartTimePicker] = useState(false);
    const [showEndTimePicker, setShowEndTimePicker] = useState(false);

    // Form state
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [location, setLocation] = useState('');
    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(new Date());
    const [allDay, setAllDay] = useState(false);
    const [notes, setNotes] = useState('');

    useImperativeHandle(ref, () => ({
      showModal: (eventData: any) => {
        setEvent(eventData);
        // Populate form with event data
        setTitle(eventData.title || '');
        setDescription(eventData.description || '');
        setLocation(eventData.location || '');
        setStartDate(eventData.startDate ? new Date(eventData.startDate) : new Date());
        setEndDate(eventData.endDate ? new Date(eventData.endDate) : new Date());
        setAllDay(eventData.allDay || false);
        setNotes(eventData.notes || '');
        modalRef.current?.openModal();
      },
      hideModal: () => {
        modalRef.current?.closeModal();
        resetForm();
      }
    }), []);

    const resetForm = () => {
      setEvent(null);
      setTitle('');
      setDescription('');
      setLocation('');
      setStartDate(new Date());
      setEndDate(new Date());
      setAllDay(false);
      setNotes('');
    };

    const handleSave = async () => {
      if (!title.trim()) {
        return;
      }

      setLoading(true);
      try {
        const updatedEvent = {
          title: title.trim(),
          description: description.trim(),
          location: location.trim(),
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          allDay,
          notes: notes.trim(),
          calendarId: event.calendarId
        };

        await apis.updateCalendarEntry(event._id, updatedEvent);

        modalRef.current?.closeModal();
        onEventUpdated?.();
        resetForm();
      } catch (error) {
        console.error('Error updating event:', error);
      } finally {
        setLoading(false);
      }
    };

    const handleStartDateChange = (event: any, selectedDate?: Date) => {
      setShowStartDatePicker(Platform.OS === 'ios');
      if (selectedDate) {
        setStartDate(selectedDate);
        // Auto-adjust end date if it's before start date
        if (selectedDate > endDate) {
          setEndDate(new Date(selectedDate.getTime() + 60 * 60 * 1000)); // Add 1 hour
        }
      }
    };

    const handleEndDateChange = (event: any, selectedDate?: Date) => {
      setShowEndDatePicker(Platform.OS === 'ios');
      if (selectedDate && selectedDate >= startDate) {
        setEndDate(selectedDate);
      }
    };

    const handleStartTimeChange = (event: any, selectedTime?: Date) => {
      setShowStartTimePicker(Platform.OS === 'ios');
      if (selectedTime) {
        const newStart = new Date(startDate);
        newStart.setHours(selectedTime.getHours());
        newStart.setMinutes(selectedTime.getMinutes());
        setStartDate(newStart);
      }
    };

    const handleEndTimeChange = (event: any, selectedTime?: Date) => {
      setShowEndTimePicker(Platform.OS === 'ios');
      if (selectedTime) {
        const newEnd = new Date(endDate);
        newEnd.setHours(selectedTime.getHours());
        newEnd.setMinutes(selectedTime.getMinutes());
        setEndDate(newEnd);
      }
    };

    return (
      <StyledModal
        ref={modalRef}
        title="Edit Event"
        caption="Update your calendar event details"
        style={{ zIndex: 9999 }}
      >
        <ScrollView style={[styles.container, { backgroundColor: theme ? '#fff' : '#1a1a1a' }]}>
          <YStack gap="$4" padding="$4">
            {/* Title */}
            <YStack gap="$2">
              <Label color={theme ? '#666' : '#999'} fontSize={14} fontWeight="600">
                Event Title *
              </Label>
              <Input
                value={title}
                onChangeText={setTitle}
                placeholder="Enter event title"
                backgroundColor={theme ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.05)'}
                borderColor={theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)'}
                borderRadius={8}
                fontSize={16}
              />
            </YStack>

            {/* All Day Toggle */}
            <XStack alignItems="center" justifyContent="space-between">
              <Label color={theme ? '#666' : '#999'} fontSize={14} fontWeight="600">
                All Day Event
              </Label>
              <Switch
                checked={allDay}
                onCheckedChange={setAllDay}
                backgroundColor={allDay ? '#007AFF' : theme ? '#ccc' : '#555'}
              >
                <Switch.Thumb animation="quick" />
              </Switch>
            </XStack>

            <Separator backgroundColor={theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)'} />

            {/* Start Date & Time */}
            <YStack gap="$2">
              <Label color={theme ? '#666' : '#999'} fontSize={14} fontWeight="600">
                Start Date & Time
              </Label>
              <XStack gap="$2" flexWrap="wrap">
                <Button
                  flex={1}
                  size="$3"
                  backgroundColor={theme ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.05)'}
                  color={theme ? '#000' : '#fff'}
                  borderRadius={8}
                  onPress={() => setShowStartDatePicker(true)}
                  icon={<MaterialIcons name="calendar-today" size={16} color={theme ? '#000' : '#fff'} />}
                >
                  {moment(startDate).format('MMM D, YYYY')}
                </Button>
                {!allDay && (
                  <Button
                    flex={1}
                    size="$3"
                    backgroundColor={theme ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.05)'}
                    color={theme ? '#000' : '#fff'}
                    borderRadius={8}
                    onPress={() => setShowStartTimePicker(true)}
                    icon={<MaterialIcons name="access-time" size={16} color={theme ? '#000' : '#fff'} />}
                  >
                    {moment(startDate).format('h:mm A')}
                  </Button>
                )}
              </XStack>
            </YStack>

            {/* End Date & Time */}
            <YStack gap="$2">
              <Label color={theme ? '#666' : '#999'} fontSize={14} fontWeight="600">
                End Date & Time
              </Label>
              <XStack gap="$2" flexWrap="wrap">
                <Button
                  flex={1}
                  size="$3"
                  backgroundColor={theme ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.05)'}
                  color={theme ? '#000' : '#fff'}
                  borderRadius={8}
                  onPress={() => setShowEndDatePicker(true)}
                  icon={<MaterialIcons name="calendar-today" size={16} color={theme ? '#000' : '#fff'} />}
                >
                  {moment(endDate).format('MMM D, YYYY')}
                </Button>
                {!allDay && (
                  <Button
                    flex={1}
                    size="$3"
                    backgroundColor={theme ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.05)'}
                    color={theme ? '#000' : '#fff'}
                    borderRadius={8}
                    onPress={() => setShowEndTimePicker(true)}
                    icon={<MaterialIcons name="access-time" size={16} color={theme ? '#000' : '#fff'} />}
                  >
                    {moment(endDate).format('h:mm A')}
                  </Button>
                )}
              </XStack>
            </YStack>

            <Separator backgroundColor={theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)'} />

            {/* Location */}
            <YStack gap="$2">
              <Label color={theme ? '#666' : '#999'} fontSize={14} fontWeight="600">
                Location
              </Label>
              <Input
                value={location}
                onChangeText={setLocation}
                placeholder="Add location"
                backgroundColor={theme ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.05)'}
                borderColor={theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)'}
                borderRadius={8}
                fontSize={16}
              />
            </YStack>

            {/* Description */}
            <YStack gap="$2">
              <Label color={theme ? '#666' : '#999'} fontSize={14} fontWeight="600">
                Description
              </Label>
              <TextArea
                value={description}
                onChangeText={setDescription}
                placeholder="Add description"
                backgroundColor={theme ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.05)'}
                borderColor={theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)'}
                borderRadius={8}
                minHeight={80}
                fontSize={16}
              />
            </YStack>

            {/* Notes */}
            <YStack gap="$2">
              <Label color={theme ? '#666' : '#999'} fontSize={14} fontWeight="600">
                Notes
              </Label>
              <TextArea
                value={notes}
                onChangeText={setNotes}
                placeholder="Add notes"
                backgroundColor={theme ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.05)'}
                borderColor={theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)'}
                borderRadius={8}
                minHeight={80}
                fontSize={16}
              />
            </YStack>

            {/* Action Buttons */}
            <XStack gap="$3" marginTop="$4">
              <Button
                flex={1}
                size="$4"
                backgroundColor={theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)'}
                color={theme ? '#000' : '#fff'}
                borderRadius={12}
                onPress={() => modalRef.current?.closeModal()}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                flex={1}
                size="$4"
                backgroundColor="#007AFF"
                color="white"
                borderRadius={12}
                onPress={handleSave}
                disabled={loading || !title.trim()}
                opacity={loading || !title.trim() ? 0.5 : 1}
                icon={loading ? undefined : <MaterialIcons name="check" size={18} color="white" />}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
            </XStack>
          </YStack>

          {/* Date/Time Pickers */}
          {showStartDatePicker && (
            <DateTimePicker
              value={startDate}
              mode="date"
              display={Platform.OS === 'ios' ? 'spinner' : 'default'}
              onChange={handleStartDateChange}
            />
          )}
          {showEndDatePicker && (
            <DateTimePicker
              value={endDate}
              mode="date"
              display={Platform.OS === 'ios' ? 'spinner' : 'default'}
              onChange={handleEndDateChange}
            />
          )}
          {showStartTimePicker && !allDay && (
            <DateTimePicker
              value={startDate}
              mode="time"
              display={Platform.OS === 'ios' ? 'spinner' : 'default'}
              onChange={handleStartTimeChange}
            />
          )}
          {showEndTimePicker && !allDay && (
            <DateTimePicker
              value={endDate}
              mode="time"
              display={Platform.OS === 'ios' ? 'spinner' : 'default'}
              onChange={handleEndTimeChange}
            />
          )}
        </ScrollView>
      </StyledModal>
    );
  }
);

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});

EditEventModal.displayName = 'EditEventModal';

export default EditEventModal;

